/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { getRefreshToken } from '../helpers/localstorage-helper';
import { ReportExportFinishedPayload } from '../types/shared.types';

const SOCKET_URL = process.env.NX_API_URL;

/**
 * Shared socket hook for report export notifications. Mirrors the
 * server-side `/report-export` namespace.
 *
 * Flow:
 *   1. Caller POSTs to /v1/{orders|deliveries}/report/export to get an id.
 *   2. Caller invokes `observe(id, true)` to subscribe.
 *   3. When the server finishes the export it emits `report_export_finished`,
 *      which fires `onFinished`.
 *   4. Caller then GETs /v1/.../report/export/:id for the downloadUrl /
 *      errorMessage and calls `observe(id, false)` to unsubscribe.
 */
export const useReportExportSocket = (
  onFinished: (payload: ReportExportFinishedPayload) => void
) => {
  const socketRef = useRef<any>(null);
  const refreshToken = getRefreshToken();
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  useEffect(() => {
    if (!refreshToken) return;

    const socket = io(SOCKET_URL, {
      path: '/report-export',
      transports: ['websocket'],
      auth: {
        authorization: refreshToken,
      },
    });
    socketRef.current = socket;

    socket.on('connection_successful', () => {
      console.log('Report export socket connection successful');
    });

    socket.on('observe-report-export', (data) => {
      console.log('observe-report-export ack:', data);
    });

    socket.on('report_export_finished', (data: ReportExportFinishedPayload) => {
      console.log('report_export_finished:', data);
      onFinishedRef.current(data);
    });

    socket.on('connect_error', (err) => {
      console.error('Report export socket connection error:', err.message);
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const observe = (reportExportId: number, subscribe: boolean) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('observe-report-export', {
        reportExportId,
        subscribe,
      });
    }
  };

  return { observe };
};
